import { useRef, useState } from "react";
import { api } from "@/lib/api.js";
import { I18nEditor, EMPTY_I18N } from "@/components/I18nEditor.js";

type CTA_TYPE = "single" | "group" | "wide_dot";
type CtaAction = { key: string; url: string };

interface CardBlockEditorProps {
  config: Record<string, string>;
  i18n: typeof EMPTY_I18N;
  onConfigChange: (v: Record<string, string>) => void;
  onI18nChange: (v: typeof EMPTY_I18N) => void;
}

function parseActions(raw: string | undefined): CtaAction[] {
  try { return raw ? (JSON.parse(raw) as CtaAction[]) : []; } catch { return []; }
}

export function CardBlockEditor({ config, i18n, onConfigChange, onI18nChange }: CardBlockEditorProps) {
  const hasPhoto = config.has_photo === "true";
  const hasText = config.has_text !== "false"; // default on
  const hasCta = config.has_cta === "true";
  const ctaType = (config.cta_type as CTA_TYPE) || "single";
  const actions = parseActions(config.cta_actions);

  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.postForm<{ url: string }>("/api/admin/upload", fd);
      onConfigChange({ ...config, photo_url: res.url });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function updateAction(index: number, field: "url", value: string) {
    const next = actions.map((a, i) => i === index ? { ...a, [field]: value } : a);
    onConfigChange({ ...config, cta_actions: JSON.stringify(next) });
  }

  function addAction() {
    const key = `action_${actions.length}`;
    const next = [...actions, { key, url: "" }];
    onConfigChange({ ...config, cta_actions: JSON.stringify(next) });
    onI18nChange({
      ko: { ...i18n.ko, [key]: "" },
      en: { ...i18n.en, [key]: "" },
      zh: { ...i18n.zh, [key]: "" },
      ja: { ...i18n.ja, [key]: "" },
    });
  }

  function removeAction(index: number) {
    const removed = actions[index];
    const next = actions.filter((_, i) => i !== index);
    onConfigChange({ ...config, cta_actions: JSON.stringify(next) });
    const strip = (obj: Record<string, string>) => { const n = { ...obj }; delete n[removed.key]; return n; };
    onI18nChange({
      ko: strip(i18n.ko),
      en: strip(i18n.en),
      zh: strip(i18n.zh),
      ja: strip(i18n.ja),
    });
  }

  const textI18nFields = [
    { key: "title", label: "제목" },
    { key: "body", label: "본문", multiline: true as const },
    { key: "annotation", label: "주석" },
  ];

  const ctaLabelField = [{ key: "cta_label", label: "버튼 레이블" }];

  const actionI18nFields = actions.map((a) => ({ key: a.key, label: `버튼 "${a.key}" 레이블` }));

  return (
    <div className="space-y-4">
      {/* 섹션 토글 */}
      <div className="flex gap-3">
        {[
          { key: "has_photo", label: "📷 사진", value: hasPhoto },
          { key: "has_text", label: "📝 텍스트", value: hasText },
          { key: "has_cta", label: "🔘 CTA", value: hasCta },
        ].map(({ key, label, value }) => (
          <button
            key={key}
            type="button"
            onClick={() => onConfigChange({ ...config, [key]: value ? "false" : "true" })}
            className={`flex-1 rounded-lg border py-2 text-xs font-semibold transition-colors ${
              value ? "border-brand-400 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-400 hover:border-gray-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 사진 섹션 */}
      {hasPhoto && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500">사진</p>
          {config.photo_url && (
            <img src={config.photo_url} alt="" className="h-32 w-full rounded-lg object-cover ring-1 ring-black/10" />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="rounded-lg border border-dashed border-brand-400 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50">
              {uploading ? "업로드 중..." : "파일 업로드"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
          </div>
          <input type="url" value={config.photo_url ?? ""} onChange={(e) => onConfigChange({ ...config, photo_url: e.target.value })}
            placeholder="https://..."
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-brand-400" />
        </div>
      )}

      {/* 텍스트 섹션 */}
      {hasText && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500">텍스트 (다국어)</p>
          <I18nEditor value={i18n} fields={textI18nFields} onChange={onI18nChange} />
        </div>
      )}

      {/* CTA 섹션 */}
      {hasCta && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500">CTA</p>

          {/* CTA 유형 선택 */}
          <div className="flex gap-2">
            {([
              { value: "single", label: "단일 버튼" },
              { value: "group", label: "그룹 (...)" },
              { value: "wide_dot", label: "긴버튼 + ..." },
            ] as { value: CTA_TYPE; label: string }[]).map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => onConfigChange({ ...config, cta_type: value })}
                className={`flex-1 rounded-lg border py-1.5 text-xs font-medium transition-colors ${
                  ctaType === value ? "border-brand-400 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* 단일 / wide_dot 메인 버튼 URL */}
          {(ctaType === "single" || ctaType === "wide_dot") && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                {ctaType === "wide_dot" ? "메인 버튼 URL" : "URL"}
              </label>
              <input type="text" value={config.cta_url ?? ""}
                onChange={(e) => onConfigChange({ ...config, cta_url: e.target.value })}
                placeholder="/s/... 또는 https://..."
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-brand-400" />
              <div className="mt-2">
                <I18nEditor value={i18n} fields={ctaLabelField} onChange={onI18nChange} />
              </div>
            </div>
          )}

          {/* 그룹 / wide_dot 액션 목록 */}
          {(ctaType === "group" || ctaType === "wide_dot") && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500">
                {ctaType === "wide_dot" ? "... 버튼 액션 목록" : "액션 목록"}
              </p>
              {actions.map((action, i) => (
                <div key={action.key} className="flex gap-2 items-center">
                  <input type="text" value={action.url}
                    onChange={(e) => updateAction(i, "url", e.target.value)}
                    placeholder="URL"
                    className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-brand-400" />
                  <button type="button" onClick={() => removeAction(i)}
                    className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                </div>
              ))}
              {actionI18nFields.length > 0 && (
                <I18nEditor value={i18n} fields={actionI18nFields} onChange={onI18nChange} />
              )}
              <button type="button" onClick={addAction}
                className="text-xs text-brand-500 hover:underline">+ 액션 추가</button>
            </div>
          )}

          {/* Coming soon 토글 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox"
              checked={config.cta_coming_soon === "true"}
              onChange={(e) => onConfigChange({ ...config, cta_coming_soon: e.target.checked ? "true" : "false" })}
              className="rounded" />
            <span className="text-xs text-gray-500">구현 예정 (준비중 표시)</span>
          </label>
        </div>
      )}
    </div>
  );
}
